import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { ChevronDownIcon } from "../../../../Icons";
import { fieldClass, stripControlClass } from "../../../Patients/components/clinicalForms/formLayout";
import {
  OTHER_PATIENT_VALUE,
  patientDisplayName,
} from "../hooks/useDailyLogPatients";

const OTHER_OPTION = {
  id: OTHER_PATIENT_VALUE,
  name: "Other",
};

/**
 * Searchable patient picker. Includes Other for unlinked free-text names.
 */
const PatientNameSelect = ({
  value,
  displayName = "",
  patients = [],
  loading = false,
  disabled = false,
  placeholder = "Select patient…",
  onSelect,
  className = "",
  compact = false,
}) => {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState(null);

  const isOther = value === OTHER_PATIENT_VALUE || value == null || value === "";
  const selected = useMemo(
    () =>
      isOther
        ? null
        : patients.find((patient) => Number(patient.id) === Number(value)) ||
          null,
    [patients, value, isOther],
  );
  const selectedLabel = selected
    ? patientDisplayName(selected)
    : isOther && displayName
      ? `Other — ${displayName}`
      : isOther
        ? "Other"
        : displayName || "";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = q
      ? patients.filter((patient) => {
          const name = patientDisplayName(patient).toLowerCase();
          const mrn = String(patient.mrn || "").toLowerCase();
          return name.includes(q) || mrn.includes(q);
        })
      : patients;
    return [...rows, OTHER_OPTION];
  }, [patients, query]);

  const updateMenuPosition = () => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const preferUp = spaceBelow < 240 && rect.top > spaceBelow;
    const maxHeight = Math.min(256, preferUp ? rect.top - 8 : spaceBelow - 8);
    setMenuStyle({
      position: "fixed",
      left: rect.left,
      width: Math.max(rect.width, 240),
      zIndex: 80,
      maxHeight: Math.max(120, maxHeight),
      ...(preferUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      const t = event.target;
      if (rootRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
      setQuery("");
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (patient) => {
    if (patient.id === OTHER_PATIENT_VALUE) {
      onSelect?.({ id: null, name: "", other: true });
    } else {
      onSelect?.({
        id: Number(patient.id),
        name: patientDisplayName(patient),
        other: false,
      });
    }
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`${compact ? stripControlClass : fieldClass} ${compact ? "pr-5" : "pr-7"} ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
          value={open ? query : selectedLabel}
          placeholder={loading ? "Loading patients…" : placeholder}
          disabled={disabled || loading}
          readOnly={!open}
          onFocus={() => {
            if (disabled || loading) return;
            setOpen(true);
            setQuery("");
          }}
          onClick={() => {
            if (disabled || loading) return;
            setOpen(true);
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            if (!open) setOpen(true);
          }}
          autoComplete="off"
        />
        <ChevronDownIcon
          size={compact ? 12 : 14}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
      </div>
      {open && menuStyle && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={menuRef}
              style={menuStyle}
              className="overflow-auto rounded-md border border-indigo-200 bg-white shadow-lg"
            >
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-[12px] text-slate-400">
                  No patients found
                </div>
              ) : (
                filtered.map((patient) => {
                  const name = patientDisplayName(patient) || patient.name;
                  const other = patient.id === OTHER_PATIENT_VALUE;
                  const active = other
                    ? isOther
                    : Number(patient.id) === Number(value);
                  return (
                    <button
                      key={String(patient.id)}
                      type="button"
                      className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[12px] ${
                        active
                          ? "bg-indigo-50 text-indigo-800"
                          : "text-slate-800 hover:bg-slate-50"
                      } ${other ? "border-t border-slate-100" : ""}`}
                      onClick={() => pick(patient)}
                    >
                      <span className="truncate font-medium">{name || "—"}</span>
                      {patient.mrn && !other ? (
                        <span className="shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                          {patient.mrn}
                        </span>
                      ) : other ? (
                        <span className="shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                          Not linked
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

PatientNameSelect.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  displayName: PropTypes.string,
  patients: PropTypes.array,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  onSelect: PropTypes.func,
  className: PropTypes.string,
  compact: PropTypes.bool,
};

export default PatientNameSelect;
